/**
 * Declarative tutorial catalogue.
 *
 * Keep view routing, target selectors, and copy keys here. The rendering engine
 * deliberately knows nothing about individual product pages. Increment a
 * tutorial's version when existing users should see materially changed steps.
 */
export const TUTORIALS = [
  {
    id: 'landing.overview',
    version: 1,
    view: 'landing',
    titleKey: 'tutorial.landing.name',
    steps: [
      { target: '#home .hero-ads-content', titleKey: 'tutorial.landing.hero.title', bodyKey: 'tutorial.landing.hero.body', placement: 'bottom' },
      { target: '#premium-intro-title', titleKey: 'tutorial.landing.intro.title', bodyKey: 'tutorial.landing.intro.body' },
      { target: '#premium-features-title', titleKey: 'tutorial.landing.features.title', bodyKey: 'tutorial.landing.features.body' },
      { target: '#premium-workflow-title', titleKey: 'tutorial.landing.workflow.title', bodyKey: 'tutorial.landing.workflow.body' },
      { target: '#contact-form .section-heading', titleKey: 'tutorial.landing.contact.title', bodyKey: 'tutorial.landing.contact.body', placement: 'top' },
    ],
  },
  {
    id: 'workspace.campaign',
    version: 1,
    view: 'workspace',
    titleKey: 'tutorial.workspace.name',
    steps: [
      { target: '.campaign-page-head h2', titleKey: 'tutorial.workspace.intro.title', bodyKey: 'tutorial.workspace.intro.body' },
      { target: '.brief-head', titleKey: 'tutorial.workspace.brief.title', bodyKey: 'tutorial.workspace.brief.body' },
      { target: '[data-tour="studio-step-brand"]', titleKey: 'tutorial.workspace.brand.title', bodyKey: 'tutorial.workspace.brand.body', allowInteraction: true },
      { target: '[data-tour="studio-step-audience"]', titleKey: 'tutorial.workspace.audience.title', bodyKey: 'tutorial.workspace.audience.body', allowInteraction: true },
      { target: '[data-tour="studio-step-output"]', titleKey: 'tutorial.workspace.output.title', bodyKey: 'tutorial.workspace.output.body', allowInteraction: true },
      { target: '.brief-footer', titleKey: 'tutorial.workspace.generate.title', bodyKey: 'tutorial.workspace.generate.body', placement: 'top' },
      { target: '.saved-panel .section-heading', titleKey: 'tutorial.workspace.saved.title', bodyKey: 'tutorial.workspace.saved.body' },
    ],
  },
  {
    id: 'brandhub.setup',
    version: 1,
    view: 'brandhub',
    titleKey: 'tutorial.brandhub.name',
    steps: [
      { target: '[data-tour="brandhub-header"]', titleKey: 'tutorial.brandhub.intro.title', bodyKey: 'tutorial.brandhub.intro.body' },
      { target: '[data-tour="brandhub-company"] .section-heading', titleKey: 'tutorial.brandhub.company.title', bodyKey: 'tutorial.brandhub.company.body' },
      { target: '[data-tour="brandhub-products"] .section-heading', titleKey: 'tutorial.brandhub.products.title', bodyKey: 'tutorial.brandhub.products.body' },
      { target: '[data-tour="brandhub-assets"] .section-heading', titleKey: 'tutorial.brandhub.assets.title', bodyKey: 'tutorial.brandhub.assets.body' },
      { target: '[data-tour="brandhub-generate"] .section-heading', titleKey: 'tutorial.brandhub.generate.title', bodyKey: 'tutorial.brandhub.generate.body' },
    ],
  },
  {
    id: 'editor.graphics',
    version: 1,
    view: 'editor',
    titleKey: 'tutorial.editor.name',
    steps: [
      { target: '[data-tour="editor-toolbar"] > button:first-child', titleKey: 'tutorial.editor.toolbar.title', bodyKey: 'tutorial.editor.toolbar.body' },
      { target: '[data-tour="editor-assets"]', titleKey: 'tutorial.editor.assets.title', bodyKey: 'tutorial.editor.assets.body' },
      { target: '[data-tour="editor-tools"] .editor-tool-btn:first-child', titleKey: 'tutorial.editor.tools.title', bodyKey: 'tutorial.editor.tools.body', allowInteraction: true },
      { target: ['[data-tour="editor-canvas"] > div', '[data-tour="editor-canvas"] canvas'], titleKey: 'tutorial.editor.canvas.title', bodyKey: 'tutorial.editor.canvas.body', maxSpotlightWidth: 420, maxSpotlightHeight: 260 },
      { target: ['[data-tour="editor-properties"] h4', '[data-tour="editor-properties"]'], titleKey: 'tutorial.editor.properties.title', bodyKey: 'tutorial.editor.properties.body' },
    ],
  },
  {
    id: 'analytics.insights',
    version: 1,
    view: 'dashboard',
    titleKey: 'tutorial.analytics.name',
    steps: [
      { target: '#dashboard-view .campaign-page-head h2', titleKey: 'tutorial.analytics.intro.title', bodyKey: 'tutorial.analytics.intro.body' },
      { target: ['.dash-stats', '.dash-cta'], titleKey: 'tutorial.analytics.metrics.title', bodyKey: 'tutorial.analytics.metrics.body' },
      { target: ['.dash-grid .card-title-row', '.dash-cta h3'], titleKey: 'tutorial.analytics.patterns.title', bodyKey: 'tutorial.analytics.patterns.body' },
    ],
  },
  {
    id: 'workspace.results',
    version: 1,
    view: 'workspace',
    requiredTarget: '.cs-output',
    titleKey: 'tutorial.results.name',
    steps: [
      { target: '.cs-summary-title', titleKey: 'tutorial.results.summary.title', bodyKey: 'tutorial.results.summary.body' },
      { target: '.cs-toolbar', titleKey: 'tutorial.results.actions.title', bodyKey: 'tutorial.results.actions.body' },
      { target: '.cs-tabs', titleKey: 'tutorial.results.tabs.title', bodyKey: 'tutorial.results.tabs.body', allowInteraction: true },
      { target: ['.cs-panel .card-title-row', '.cs-panel article h3'], titleKey: 'tutorial.results.detail.title', bodyKey: 'tutorial.results.detail.body' },
      { target: '.feedback-panel .card-title-row', optional: true, titleKey: 'tutorial.results.feedback.title', bodyKey: 'tutorial.results.feedback.body' },
    ],
  },
  {
    id: 'workspace.experiment',
    version: 1,
    view: 'workspace',
    requiredTarget: '.experiment-panel',
    titleKey: 'tutorial.experiment.name',
    steps: [
      { target: '.experiment-header', titleKey: 'tutorial.experiment.intro.title', bodyKey: 'tutorial.experiment.intro.body' },
      { target: '.comparison-cockpit-head', titleKey: 'tutorial.experiment.compare.title', bodyKey: 'tutorial.experiment.compare.body' },
      { target: '.experiment-winner-card', optional: true, titleKey: 'tutorial.experiment.winner.title', bodyKey: 'tutorial.experiment.winner.body' },
    ],
  },
  {
    id: 'settings.preferences',
    version: 1,
    view: '*',
    manualOnly: true,
    titleKey: 'tutorial.settings.name',
    steps: [
      { target: '[data-tour="settings-language"]', titleKey: 'tutorial.settings.language.title', bodyKey: 'tutorial.settings.language.body', allowInteraction: true },
      { target: '[data-tour="settings-theme"]', titleKey: 'tutorial.settings.theme.title', bodyKey: 'tutorial.settings.theme.body', allowInteraction: true },
      { target: '[data-tour="settings-notifications"]', titleKey: 'tutorial.settings.notifications.title', bodyKey: 'tutorial.settings.notifications.body', allowInteraction: true },
      { target: '[data-tour="settings-tutorials"] .settings-card-title', titleKey: 'tutorial.settings.replay.title', bodyKey: 'tutorial.settings.replay.body' },
    ],
  },
];

export const getTutorial = (tutorialId) => TUTORIALS.find(({ id }) => id === tutorialId);

export function findTarget(target) {
  const selectors = Array.isArray(target) ? target : [target];
  for (const selector of selectors) {
    if (!selector) continue;
    try {
      const element = document.querySelector(selector);
      if (element) return element;
    } catch (error) {
      // A malformed page-owned selector must never block the interface.
    }
  }
  return null;
}
